import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { supabase } from '../services/supabaseAudioService';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import InterfaceLanguageToggle from './InterfaceLanguageToggle';
import './LandingPage.css';

import { EmojiText, Picto } from './common/Picto';
interface LandingPageProps {
  onLogin: (response: any) => void;
  onContinueAsGuest: () => void;
}

const LANG_STRIP = [
  { flag: '🇸🇳', name: 'WOLOF'   },
  { flag: '🇲🇱', name: 'BAMBARA' },
  { flag: '🇨🇩', name: 'LINGALA' },
  { flag: '🌍',  name: 'PEUL'    },
  { flag: '🌍',  name: 'SWAHILI' },
  { flag: '🇳🇬', name: 'YORUBA'  },
  { flag: '🏛️', name: 'COPTE'   },
];

const LandingPage: React.FC<LandingPageProps> = ({ onLogin, onContinueAsGuest }) => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  const VOCAB_ROWS = [
    { fr: t('landing.vocabWords.hello'),     wf: 'Salamalekum',    ba: 'I ni ce',     la: 'Mbote'         },
    { fr: t('landing.vocabWords.thanks'),    wf: 'Jërejëf',         ba: 'Aw ni baara', la: 'Matondo'       },
    { fr: t('landing.vocabWords.goodbye'),   wf: 'Ba beneen yoon',  ba: 'Kà taa',      la: 'Tokomonana'    },
    { fr: t('landing.vocabWords.doingWell'), wf: 'Maa ngi fi rekk', ba: 'N ka kɛnɛ',   la: 'Nazali malamu' },
    { fr: t('landing.vocabWords.water'),     wf: 'Ndox',            ba: 'Ji',          la: 'Mai'           },
    { fr: t('landing.vocabWords.family'),    wf: 'Mbokk yi',        ba: 'Somɔgɔw',     la: 'Libota'        },
  ];

  const FEATURES = [
    { icon: '🗂️', title: t('landing.features.flashcards.title'),    desc: t('landing.features.flashcards.desc') },
    { icon: '✏️', title: t('landing.features.quiz.title'),          desc: t('landing.features.quiz.desc') },
    { icon: '💬', title: t('landing.features.conversations.title'), desc: t('landing.features.conversations.desc') },
    { icon: '📖', title: t('landing.features.grammar.title'),       desc: t('landing.features.grammar.desc') },
    { icon: '🎵', title: t('landing.features.songs.title'),         desc: t('landing.features.songs.desc') },
    { icon: '🏆', title: t('landing.features.progression.title'),   desc: t('landing.features.progression.desc') },
  ];

  useEffect(() => { setMounted(true); }, []);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const isNative = Capacitor.isNativePlatform();
      const redirectUrl = isNative ? 'com.jogolinga.com://login-callback' : window.location.origin;
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: { access_type: 'offline', prompt: 'consent' },
          skipBrowserRedirect: isNative,
        },
      });
      if (error) throw error;
      if (isNative && data?.url) {
        await Browser.open({ url: data.url, windowName: '_blank', presentationStyle: 'fullscreen', toolbarColor: 'var(--gold-primary)' });
        setIsLoading(false);
      }
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <div className={`lp-root ${theme}`}>

      {/* ── Barre de navigation ── */}
      <nav className="lp-nav">
        <div className="lp-nav__logo">
          <img src="/assets/logo-tile.svg" alt="JogoLinga" className="lp-nav__logo-img" />
          <span className="lp-nav__logo-text">JogoLinga</span>
        </div>
        <div className="lp-nav__right">
          <InterfaceLanguageToggle dark={theme === 'dark'} />
          <motion.div
            className="lp-theme-btn"
            onClick={toggleTheme}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <EmojiText text={theme === 'dark' ? '☀️' : '🌙'} />
          </motion.div>
        </div>
      </nav>

      {/* ── HERO deux colonnes ── */}
      <motion.section
        className="lp-hero"
        initial={{ opacity: 0, y: 24 }}
        animate={mounted ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        {/* Colonne illustration */}
        <div className="lp-hero__illus">
          <div className="lp-bogolan-ring lp-bogolan-ring--1" />
          <div className="lp-bogolan-ring lp-bogolan-ring--2" />
          <div className="lp-bogolan-ring lp-bogolan-ring--3" />
          <img
            src="/assets/logo.jpg"
            alt={t('landing.hero.mascotAlt') as string}
            className="lp-hero__mascot"
          />
          {/* Mots flottants décoratifs */}
          <span className="lp-float lp-float--1">Salamalekum</span>
          <span className="lp-float lp-float--2">Jërejëf</span>
          <span className="lp-float lp-float--3">I ni ce</span>
          <span className="lp-float lp-float--4">Mbote</span>
          <span className="lp-float lp-float--5">Asante</span>
          <span className="lp-float lp-float--6">Jam waali</span>
          <span className="lp-float lp-float--7">Matondo</span>
          <span className="lp-float lp-float--8">Kà taa</span>
        </div>

        {/* Colonne texte + CTA */}
        <div className="lp-hero__content">
          <h1 className="lp-hero__title">
            {t('landing.hero.title')}
          </h1>
          <p className="lp-hero__sub">
            {t('landing.hero.subtitle')}
          </p>

          <motion.button
            className="lp-btn-primary"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            {isLoading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <span className="lp-spinner" />
                {t('landing.hero.connecting')}
              </span>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <svg width="20" height="20" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
                  <path fill="currentColor" d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"/>
                  <path fill="currentColor" d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.1.83-.64 2.08-1.84 2.92l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.62z"/>
                  <path fill="currentColor" d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z"/>
                  <path fill="currentColor" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.97 13.04C2.45 15.98 5.48 18 9 18z"/>
                </svg>
                {t('landing.hero.ctaStart')}
              </span>
            )}
          </motion.button>

          <div className="lp-divider"><span>{t('landing.hero.or')}</span></div>

          <div className="lp-google-wrap">
            <motion.button
              className="lp-btn-guest"
              onClick={onContinueAsGuest}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              {t('landing.hero.continueGuest')}
            </motion.button>
          </div>

          <div className="lp-hero__stats">
            <div className="lp-stat"><strong>7</strong><span>{t('landing.hero.statLanguages')}</span></div>
            <div className="lp-stat"><strong>500+</strong><span>{t('landing.hero.statWords')}</span></div>
            <div className="lp-stat"><strong>100&nbsp;%</strong><span>{t('landing.hero.statFree')}</span></div>
          </div>
        </div>
      </motion.section>

      {/* ── Bandeau langues ── */}
      <div className="lp-lang-strip">
        {LANG_STRIP.map(l => (
          <div key={l.name} className="lp-lang-strip__item">
            <span className="lp-lang-strip__name">{l.name}</span>
          </div>
        ))}
      </div>

      {/* ════════ CONTENU ÉDITORIAL (indexable Google) ════════ */}
      <div className="lp-editorial">

        {/* Pourquoi */}
        <section className="lp-section">
          <h2 className="lp-section-title">{t('landing.why.title')}</h2>
          <div className="lp-prose">
            <p>
              {t('landing.why.p1Before')}<strong>{t('landing.why.p1Strong')}</strong>{t('landing.why.p1After')}
            </p>
            <p>
              {t('landing.why.p2Before')}<strong>{t('landing.why.p2Strong')}</strong>{t('landing.why.p2After')}
            </p>
            <p>
              {t('landing.why.p3Before')}<strong>{t('landing.why.p3Strong')}</strong>{t('landing.why.p3After')}
            </p>
          </div>
        </section>

        {/* Guides des langues */}
        <section className="lp-section">
          <h2 className="lp-section-title">{t('landing.guides.title')}</h2>
          <div className="lp-guides-grid">
            <Link to="/guide/wolof" className="lp-guide-card">
              <span className="lp-guide-card__flag">🇸🇳</span>
              <strong className="lp-guide-card__title">{t('landing.guides.wolofTitle')}</strong>
              <p className="lp-guide-card__desc">
                {t('landing.guides.wolofDesc')}
              </p>
              <span className="lp-guide-card__cta">{t('landing.guides.cta')}</span>
            </Link>
            <Link to="/guide/bambara" className="lp-guide-card">
              <span className="lp-guide-card__flag">🇲🇱</span>
              <strong className="lp-guide-card__title">{t('landing.guides.bambaraTitle')}</strong>
              <p className="lp-guide-card__desc">
                {t('landing.guides.bambaraDesc')}
              </p>
              <span className="lp-guide-card__cta">{t('landing.guides.cta')}</span>
            </Link>
            <Link to="/guide/swahili" className="lp-guide-card">
              <span className="lp-guide-card__flag">🇹🇿</span>
              <strong className="lp-guide-card__title">{t('landing.guides.swahiliTitle')}</strong>
              <p className="lp-guide-card__desc">
                {t('landing.guides.swahiliDesc')}
              </p>
              <span className="lp-guide-card__cta">{t('landing.guides.cta')}</span>
            </Link>
            <Link to="/apprendre" className="lp-guide-card">
              <span className="lp-guide-card__flag"><Picto name="livre" /></span>
              <strong className="lp-guide-card__title">{t('landing.guides.learnTitle')}</strong>
              <p className="lp-guide-card__desc">
                {t('landing.guides.learnDesc')}
              </p>
              <span className="lp-guide-card__cta">{t('landing.guides.learnCta')}</span>
            </Link>
          </div>
        </section>

        {/* Tableau vocabulaire comparatif */}
        <section className="lp-section">
          <h2 className="lp-section-title">{t('landing.vocabTable.title')}</h2>
          <div className="lp-table-wrap">
            <table className="lp-vocab-table">
              <thead>
                <tr>
                  <th>{t('landing.vocabTable.colFrench')}</th>
                  <th>Wolof</th>
                  <th>Bambara</th>
                  <th>Lingala</th>
                </tr>
              </thead>
              <tbody>
                {VOCAB_ROWS.map(r => (
                  <tr key={r.fr}>
                    <td>{r.fr}</td>
                    <td>{r.wf}</td>
                    <td>{r.ba}</td>
                    <td>{r.la}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Les 7 langues */}
        <section className="lp-section">
          <h2 className="lp-section-title">{t('landing.langsSection.title')}</h2>
          <div className="lp-langs-grid">
            {[
              { flag: '🇸🇳', name: 'Wolof',   region: t('landing.langs.wolof.region'),   speakers: t('landing.langs.wolof.speakers'),   note: t('landing.langs.wolof.note') },
              { flag: '🇲🇱', name: 'Bambara', region: t('landing.langs.bambara.region'), speakers: t('landing.langs.bambara.speakers'), note: t('landing.langs.bambara.note') },
              { flag: '🇨🇩', name: 'Lingala', region: t('landing.langs.lingala.region'), speakers: t('landing.langs.lingala.speakers'), note: t('landing.langs.lingala.note') },
              { flag: '🌍',  name: 'Peul',    region: t('landing.langs.peul.region'),    speakers: t('landing.langs.peul.speakers'),    note: t('landing.langs.peul.note') },
              { flag: '🌍',  name: 'Swahili', region: t('landing.langs.swahili.region'), speakers: t('landing.langs.swahili.speakers'), note: t('landing.langs.swahili.note') },
              { flag: '🇳🇬', name: 'Yoruba',  region: t('landing.langs.yoruba.region'),  speakers: t('landing.langs.yoruba.speakers'),  note: t('landing.langs.yoruba.note') },
              { flag: '🏛️', name: 'Copte',   region: t('landing.langs.copte.region'),   speakers: t('landing.langs.copte.speakers'),   note: t('landing.langs.copte.note') },
            ].map(l => (
              <div key={l.name} className="lp-lang-card">
                <span className="lp-lang-card__flag"><EmojiText text={l.flag} /></span>
                <strong className="lp-lang-card__name">{l.name}</strong>
                <span className="lp-lang-card__region">{l.region}</span>
                <span className="lp-lang-card__speakers">{l.speakers}</span>
                <span className="lp-lang-card__note">{l.note}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Méthode */}
        <section className="lp-section">
          <h2 className="lp-section-title">{t('landing.method.title')}</h2>
          <div className="lp-features-grid">
            {FEATURES.map(f => (
              <div key={f.title} className="lp-feature-card">
                <span className="lp-feature-card__icon"><EmojiText text={f.icon} /></span>
                <strong className="lp-feature-card__title">{f.title}</strong>
                <p className="lp-feature-card__desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

      </div>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <Link to="/apprendre">{t('landing.footer.learn')}</Link>
        <span>·</span>
        <Link to="/articles">Articles</Link>
        <span>·</span>
        <Link to="/guide/wolof">{t('landing.footer.guideWolof')}</Link>
        <span>·</span>
        <Link to="/guide/bambara">{t('landing.footer.guideBambara')}</Link>
        <span>·</span>
        <Link to="/guide/swahili">{t('landing.footer.guideSwahili')}</Link>
        <span>·</span>
        <Link to="/about">{t('landing.footer.about')}</Link>
        <span>·</span>
        <Link to="/privacy">{t('landing.footer.privacy')}</Link>
        <span>·</span>
        <Link to="/terms">{t('landing.footer.terms')}</Link>
      </footer>

      {isLoading && (
        <motion.div className="loading-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <motion.div
            className="spinner"
            animate={{ rotate: 360 }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
          />
          <p>{t('landing.loading')}</p>
        </motion.div>
      )}
    </div>
  );
};

export default LandingPage;
